import {
  type InspectionCriteria as IInspectionCriteria,
  InspectionResultType,
  InspectionPOItem
} from 'app/modules/inspection/types';
import React, { useContext } from 'react';
import { InspectionContext } from 'app/modules/inspection/contexts';
import { Collapse, Descriptions, Divider, List } from 'antd';
import { consolidateCriteria } from './helper';
import { CriteriaItem } from './item';
import classes from './Criteria.module.scss';
import get from 'lodash/get';
import { Status } from 'app/modules/ui';
import { PRESETS_INSPECTION_RESULT } from 'app/modules/inspection/constants';

type InspectionCriteriaProps = {
  criteria: IInspectionCriteria<InspectionResultType>;
  poItem: InspectionPOItem;
  poItemId: string | number;
  loading?: boolean;
};

export const InspectionCriteria = (props: InspectionCriteriaProps) => {
  const { criteria, poItem, loading } = props;
  const { progress } = useContext(InspectionContext);

  const items = React.useMemo(() => {
    if (progress && criteria) {
      const poItemProgress = progress.poItems.find(
        ({ poItemId }) => poItemId === poItem.id
      );
      const { content, scheme } = criteria;
      let parsedContent = [];
      try {
        parsedContent = JSON.parse(content);
      } catch (error) {
        parsedContent = JSON.parse(scheme);
        console.error('Error parsing content', error);
      }
      return parsedContent
        .filter(({ enabled }) => enabled)
        .map(consolidateCriteria(poItemProgress));
    }
    return [];
  }, [criteria, poItem?.id, progress]);

  const itemProgress = React.useMemo(() => {
    if (progress && criteria) {
      const poItemProgress = progress.poItems.find(
        ({ poItemId }) => poItemId === poItem.id
      );
      return poItemProgress;
    }
    return { result: 'pending', progress: '0' };
  }, [criteria, poItem.id, progress]);

  const [result, percentage, color] = React.useMemo(() => {
    const result = get(itemProgress, 'result', 'pending');
    const progress = get(itemProgress, 'percentage', '0');
    const [text, statusColor] = PRESETS_INSPECTION_RESULT.get(result) || [
      'Pending',
      'default'
    ];
    return [text, parseInt(progress), statusColor];
  }, [itemProgress]);

  return (
    <Collapse className={classes.collapse}>
      {loading ? (
        <div>Loading...</div>
      ) : (
        <Collapse.Panel
          className={classes['collapse__panel']}
          header={
            <div className={classes['collapse__panel__header']}>
              <h4>Inspection Criteria</h4>
              <Descriptions>
                <Descriptions.Item label="Item Conclusion">
                  <Status type="text" color={color as string} value={result} />
                </Descriptions.Item>
                <Descriptions.Item label="Progress">
                  {percentage}%
                </Descriptions.Item>
              </Descriptions>
            </div>
          }
          key={criteria?.id}
        >
          <Divider className={classes['collapse__panel__divider--top']} />
          <List
            header={<h3>Section:</h3>}
            dataSource={items}
            split={false}
            renderItem={(item, index) => {
              return (
                <List.Item>
                  <CriteriaItem item={item} po={poItem} ordinal={index + 1} />
                </List.Item>
              );
            }}
          ></List>
        </Collapse.Panel>
      )}
    </Collapse>
  );
};
